import { ShieldAlertIcon, UsersRoundIcon } from 'lucide-react';
import { Link, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { formatDateTime } from '@/lib/site-time';

import { useUser } from '@/components/auth/user-provider';
import { CurrentUserGroupRecovery } from '@/components/auth/group-access-recovery';
import { AdministrationSideMenu } from '@/components/tenancy/administration-side-menu';
import { AdministrationSearch } from '@/components/tenancy/administration-search';
import { PageErrorBoundary } from '@/components/layout/page-error-boundary';
import { HeaderIdentity } from '@/components/tenancy/header-identity';
import { Badge } from '@/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  administrationPageInScope,
  administrationScopeForPath,
  canOpenTenantPage,
  entityOverviewPage,
  isInactiveEntityAdministrationPath,
  tenantAdministrationLanding,
  tenantAdministrationNavigation
} from '@/features/tenancy/administration-navigation';
import { hasTenantPermission } from '@/features/tenancy/permissions';
import { tenantPath } from '@/features/tenancy/urls';

export function ZeroMembershipScreen() {
  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-xl items-center sm:px-6">
      <Card className="w-full">
        <CardHeader>
          <div className="bg-muted mb-2 flex size-11 items-center justify-center rounded-full">
            <UsersRoundIcon className="size-5" aria-hidden />
          </div>
          <CardTitle>Contact your platform administrator</CardTitle>
          <CardDescription>
            Your account is signed in, but no current organisation access is available. If you belong to an assigned Microsoft group, connect and retry below. Otherwise, ask a platform administrator to assign access.
          </CardDescription>
          <CurrentUserGroupRecovery />
          <Link className="text-sm underline" to="/tenant-management">View assigned tenant administration</Link>
        </CardHeader>
      </Card>
    </div>
  );
}

function UnavailableTenantScreen({ status, name }: { status: string; name: string }) {
  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-xl items-center sm:px-6">
      <Card className="w-full">
        <CardHeader>
          <div className="bg-destructive/10 text-destructive mb-2 flex size-11 items-center justify-center rounded-full">
            <ShieldAlertIcon className="size-5" aria-hidden />
          </div>
          <CardTitle>{name} is {status}</CardTitle>
          <CardDescription>
            This tenant is currently unavailable. Please contact your platform administrator if you believe you need access.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

function OverrideBanner({ tenantId }: { tenantId: string }) {
  const user = useUser();
  const override = user?.activeOverrides.find((item) => item.organizationId === tenantId);
  if (!override) return null;

  return (
    <div className="border-amber-300 bg-amber-50 text-amber-950 mb-5 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm dark:bg-amber-950/30 dark:text-amber-100">
      <ShieldAlertIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div>
        <p className="font-medium">Break-glass access is active</p>
        <p className="text-amber-900/80 dark:text-amber-100/80">This session expires at {formatDateTime(override.expiresAt)}. Reason: {override.reason}</p>
      </div>
    </div>
  );
}

export function TenantShell() {
  const user = useUser();
  const location = useLocation();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  if (!user) return <Navigate to="/" replace />;
  if (user.isPlatformAdmin && !user.viewAsRole && /^\/t\/[^/]+\/tenant\/exchange\/?$/.test(location.pathname)) {
    return <section className="mx-auto w-full max-w-7xl space-y-5 px-4 py-6" aria-label="Selected entity Exchange administration">
      <Link className="text-sm underline" to="/platform-admin">Back to platform administration</Link>
      <h1 className="text-2xl font-semibold">Exchange administration · {tenantSlug}</h1>
      <PageErrorBoundary resetKey={location.pathname}><Outlet /></PageErrorBoundary>
    </section>;
  }
  if (user.tenants.length === 0) return user.isPlatformAdmin ? <Navigate to="/platform-admin" replace /> : <ZeroMembershipScreen />;

  const tenant = user.tenants.find((item) => item.baseSlug === tenantSlug || item.alias === tenantSlug);
  if (!tenant) return <TenantAccessDenied />;
  if (tenantSlug !== tenant.slug) {
    return <Navigate to={{ pathname: location.pathname.replace(/^\/t\/[^/]+/, `/t/${tenant.slug}`), search: location.search, hash: location.hash }} replace />;
  }
  if (tenant.status !== 'active' && !isInactiveEntityAdministrationPath(location.pathname)) return <UnavailableTenantScreen status={tenant.status} name={tenant.name} />;

  // Product routes (home, book, mobile /today, …) share the tenant guard only.
  // Admin chrome stays on /t/:slug/tenant/* pages.
  const isTenantAdminSection = /\/t\/[^/]+\/tenant(?:\/|$)/.test(location.pathname);
  if (!isTenantAdminSection) {
    return <Outlet />;
  }

  const scope = administrationScopeForPath(location.pathname);
  const scopeLabel = scope === 'entity' ? 'Entity administration' : 'Tenant administration';
  const navigation = tenantAdministrationNavigation.filter((item) =>
    administrationPageInScope(item.to, scope) && (tenant.status === 'active' || item.availableWhenInactive) && (canOpenTenantPage(tenant.permissions, item.permissions) || (item.to === 'tenant/exchange' && user.isPlatformAdmin && !user.viewAsRole))
  );

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6">
      <OverrideBanner tenantId={tenant.id} />
      <header className="mb-6 flex flex-col justify-between gap-4 border-b pb-5 sm:flex-row sm:items-center">
        <div className="flex min-w-0 items-center gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight">{scopeLabel}</h1>
            <HeaderIdentity />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {tenant.roles.map((role) => <Badge key={role.id} variant="secondary">{role.name}</Badge>)}
        </div>
      </header>
      <div className="flex min-w-0 flex-col gap-6 md:flex-row">
        <AdministrationSideMenu
          label={`${scopeLabel} navigation`}
          items={navigation.map((item) => ({
            label: item.label,
            icon: item.icon,
            to: tenantPath(tenant.slug, item.to),
            end: item.to === entityOverviewPage
          }))}
        />
        <section aria-label={`${scopeLabel} content`} className="bg-card min-w-0 flex-1 space-y-6 rounded-xl border p-4 shadow-sm sm:p-6">
          <AdministrationSearch key={`search-${tenant.id}-${scope}`} entitySlug={tenant.slug} permissions={tenant.permissions} active={tenant.status === 'active'} scope={scope} />
          <div key={`content-${tenant.id}`}>
            <PageErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </PageErrorBoundary>
          </div>
        </section>
      </div>
    </div>
  );
}

export function TenantIndexRedirect() {
  const user = useUser();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const tenant = user?.tenants.find((item) => item.slug === tenantSlug);
  const firstPage = tenant && tenantAdministrationLanding(tenant);
  return firstPage && tenantSlug
    ? <Navigate to={tenantPath(tenantSlug, firstPage)} replace />
    : <TenantAccessDenied />;
}

function TenantAccessDenied() {
  return (
    <Card>
      <CardHeader>
        <CurrentUserGroupRecovery />
        <CardTitle>Access not available</CardTitle>
        <CardDescription>You do not have permission to view this tenant administration screen.</CardDescription>
      </CardHeader>
    </Card>
  );
}

export function TenantPermissionGate({ permission, children }: { permission: string; children: React.ReactNode }) {
  const user = useUser();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const tenant = user?.tenants.find((item) => item.slug === tenantSlug);
  if (!tenant || !hasTenantPermission(tenant.permissions, permission)) {
    return <TenantAccessDenied />;
  }
  return <>{children}</>;
}

export function TenantGuardedPage({ permission, children }: { permission: string; children: React.ReactNode }) {
  return <TenantPermissionGate permission={permission}>{children}</TenantPermissionGate>;
}

export function TenantGuardedAnyPermission({ permissions, children }: { permissions: readonly string[]; children: React.ReactNode }) {
  const user = useUser();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const tenant = user?.tenants.find((item) => item.slug === tenantSlug);
  if (!tenant || !permissions.some((permission) => hasTenantPermission(tenant.permissions, permission))) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access not available</CardTitle>
          <CardDescription>You do not have permission to view this tenant administration screen.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return <>{children}</>;
}
